import { Injectable } from '@angular/core';
import {
    FileDataRequest,
    IPatientCase,
    IPatientDataField,
    Patient,
    PatientCase,
    PatientDataField,
    PatientRegisterResult,
    PatientSearchAttributes,
} from '@delab/ngx-gen-ts-ixserv/models';
import { DelabNgxPatientsServiceDefinition } from '@delab/ngx-ixconnect-components';
import { BehaviorSubject, Observable, catchError, defer, map, of, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { UserInfoDto } from '../../../../.api-client/models/user-info-dto';
import { OrdersApiService } from '../../../../.api-client/services/orders-api.service';
import { PatientApiService } from '../../../../.api-client/services/patient-api.service';

@Injectable({
    providedIn: 'root',
})
export class PatientsService implements DelabNgxPatientsServiceDefinition {
    private readonly _patientSource = new BehaviorSubject<PatientCase | undefined>(undefined);
    public readonly patientObs = this._patientSource.asObservable();

    public set patient(value: PatientCase) {
        this._patientSource.next(value);
    }

    public resetPatient() {
        this._patientSource.next(undefined);
    }

    constructor(
        private readonly _patientApiService: PatientApiService,
        private readonly _ordersApiService: OrdersApiService,
    ) {}

    public getAndSetDummyPatient(userInfo: UserInfoDto): Observable<PatientCase | undefined> {
        //Name from sso is always in 3 parts, xccExtId Name and Firstname - all in one subject split with ' '. Only take the first part for username!

        // const userName = userInfo?.usrName?.split(' ')[0];
        const userName = userInfo.usrName;

        if (!userInfo.usrName) {
            throw new Error('No username found!');
        }

        const dummyUserSearchAttributes = new PatientSearchAttributes({
            perName: 'Dummy',
            perFName: userName,
        });

        return this.searchPatient(dummyUserSearchAttributes).pipe(
            switchMap((patient) => {
                if (!patient || patient.length === 0) {
                    // no patient -> register new
                    return this._registerDummyPatient(dummyUserSearchAttributes).pipe(
                        map(({ patient, cas }) => {
                            const tempPatient: IPatientCase = {
                                cas: cas?.toJSON(),
                                patient: patient?.toJSON(),
                            };

                            return [new PatientCase(tempPatient)];
                        }),
                    );
                }

                return of(patient);
            }),
            map((patients) => {
                if (!patients || patients.length === 0) {
                    return undefined;
                }

                this._patientSource.next(patients[0]);
                return patients[0];
            }),
        );
    }

    private _registerDummyPatient(dummyAttributes: PatientSearchAttributes): Observable<PatientRegisterResult> {
        return defer(() => {
            const additionalData: IPatientDataField[] = [
                { type: 'FirstName', value: dummyAttributes.perFName },
                { type: 'LastName', value: dummyAttributes.perName },
            ];
            const body = { additionalData: additionalData };

            return this._ordersApiService.ordersRegisterPatient({ body });
        }).pipe(
            map((patientRegisterResult) => {
                if (!patientRegisterResult) {
                    throw new Error('No patientRegisterResult received');
                }
                return new PatientRegisterResult(patientRegisterResult);
            }),
            catchError((error: any) => {
                console.error('Error registering patient:', error);
                throw new Error('Error registering patient');
            }),
        );
    }

    getPatientCase(casId: number): Observable<PatientCase> {
        return this._patientApiService.patientGetPatientCase({ 'x-trace-id': uuidv4(), casId: casId }).pipe(
            map((patientCase) => {
                if (patientCase === undefined) {
                    throw new Error('No patient case');
                }

                return new PatientCase(patientCase);
            }),
        );
    }

    getPatient(recId: number): Observable<Patient> {
        return this._patientApiService.patientGetPatientForRecId({ 'x-trace-id': uuidv4(), recId: recId }).pipe(
            map((patient) => {
                if (patient === undefined) {
                    throw new Error('No patient');
                }

                return new Patient(patient);
            }),
        );
    }

    getCasInfo(casId: number): Observable<PatientDataField[]> {
        return this._patientApiService.patientGetCasInfo({ 'x-trace-id': uuidv4(), casId: casId }).pipe(
            map((casInfo) => {
                if (casInfo === undefined) {
                    throw new Error('No patient case info');
                }
                return casInfo.map((patientDataField) => new PatientDataField(patientDataField));
            }),
        );
    }

    readFileData(request: FileDataRequest): Observable<PatientDataField[][]> {
        return this._patientApiService.patientReadFileData({ 'x-trace-id': uuidv4(), body: request }).pipe(
            map((patients) => {
                if (patients === undefined) {
                    throw new Error('Empty response');
                }
                return patients.map((patientDataFields) => patientDataFields.map((value) => new PatientDataField(value)));
            }),
        );
    }

    searchPatient(filter: PatientSearchAttributes): Observable<PatientCase[]> {
        return this._patientApiService.patientSearchPatient({ 'x-trace-id': uuidv4(), body: filter }).pipe(
            map((patientCaseDtos) => {
                if (patientCaseDtos === undefined) {
                    throw new Error('No patients');
                }
                return patientCaseDtos.map((patientCaseDto) => new PatientCase(patientCaseDto));
            }),
        );
    }
}
